<template>
	<section class="v2rt">
		<div class="v2rt-card">
			<div class="v2rt-card__head">
				<div class="v2rt-card__icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
				</div>
				<div>
					<div class="v2rt-card__title">{{ $t('campaign.details_tab') | capitalize }}</div>
					<div class="v2rt-card__sub">Read-only metadata submitted by the DSP</div>
				</div>
			</div>

			<!-- Campaign top section (non-external) -->
			<template v-if="!instance.is_external">
				<div class="v2rt-details-grid v2rt-details-grid--top">
					<!-- Campaign Name (editable for DSP) -->
					<div class="v2rt-field v2rt-field--full">
						<div class="v2rt-field-label">{{ $t('campaign.campaign_name') | capitalize }}</div>
						<div v-if="isNameEditModeOn && appSide === 'dsp'" class="v2rt-name-edit">
							<input
								v-model="nameTempValue"
								class="v2rt-input"
								type="text"
								:placeholder="$t('campaign.campaign_name') | capitalize"
							/>
							<button class="v2rt-icon-btn v2rt-icon-btn--ok" @click="applyNameChanges">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
							</button>
							<button class="v2rt-icon-btn v2rt-icon-btn--cancel" @click="cancelNameChanges">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
							</button>
						</div>
						<div v-else class="v2rt-field-value v2rt-field-value--big">
							<span>{{ nameValue }}</span>
							<button
								v-if="appSide === 'dsp'"
								class="v2rt-icon-btn v2rt-icon-btn--edit"
								@click="editName"
							>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
							</button>
						</div>
					</div>

					<div class="v2rt-field">
						<div class="v2rt-field-label">{{ $t('campaign.ad_flight') | capitalize }}</div>
						<div class="v2rt-field-value v2rt-field-value--mono">
							{{ adFlight || '—' }} <span class="v2rt-units">{{ $tc('campaign.days_won', adFlight) }}</span>
						</div>
					</div>

					<div class="v2rt-field">
						<div class="v2rt-field-label">{{ $t('campaign.ad_duration') | capitalize }}</div>
						<div class="v2rt-field-value v2rt-field-value--mono">
							{{ instance.ad_duration || '—' }} <span class="v2rt-units">{{ $t('campaign.sec') }}</span>
						</div>
					</div>

					<div class="v2rt-field">
						<div class="v2rt-field-label">{{ $t('campaign.time_period') | capitalize }}</div>
						<div class="v2rt-field-value v2rt-field-value--mono">{{ timePeriod || '—' }}</div>
					</div>
				</div>

				<div class="v2rt-divider" />
			</template>

			<!-- DSP / Contact info section -->
			<div class="v2rt-details-grid">
				<div class="v2rt-field">
					<div class="v2rt-field-label">DSP</div>
					<div class="v2rt-field-value">{{ instance.owner_instance ? instance.owner_instance.name : '—' }}</div>
				</div>
				<div class="v2rt-field">
					<div class="v2rt-field-label">{{ $t('campaign.created_by') | capitalize }}</div>
					<div class="v2rt-field-value">{{ createdBy }}</div>
				</div>
				<div class="v2rt-field">
					<div class="v2rt-field-label">{{ $t('campaign.email') | capitalize }}</div>
					<a v-if="contactEmail" :href="`mailto:${contactEmail}`" target="_blank" class="v2rt-field-value">
						{{ contactEmail }}
					</a>
					<div v-else class="v2rt-field-value">&mdash;</div>
				</div>
				<div class="v2rt-field">
					<div class="v2rt-field-label">{{ $t('campaign.contact_number') | capitalize }}</div>
					<div class="v2rt-field-value v2rt-field-value--mono">{{ contactPhoneNumber }}</div>
				</div>
				<div class="v2rt-field v2rt-field--full">
					<div class="v2rt-field-label">{{ $t('campaign.address') | capitalize }}</div>
					<div class="v2rt-field-value">
						{{ instance.owner_instance && instance.owner_instance.address ? instance.owner_instance.address : '—' }}
					</div>
				</div>
				<div class="v2rt-field">
					<div class="v2rt-field-label">{{ $t('advertiser.advertiser_column_label') | capitalize }}</div>
					<div class="v2rt-field-value">
						<a
							v-if="instance.advertiser"
							class="v2rt-link"
							@click="editAdvertiser(instance.advertiser_instance)"
						>{{ instance.advertiser_instance.name }}</a>
						<span v-else>—</span>
						<span v-if="instance.advertiser" class="v2rt-pill" :class="advertiserPillClass">{{ advertiserPillLabel }}</span>
					</div>
				</div>
				<div class="v2rt-field">
					<div class="v2rt-field-label">{{ $t('campaign.industry') | capitalize }}</div>
					<div class="v2rt-field-value">{{ instance.industry_instance ? instance.industry_instance.name : '—' }}</div>
				</div>
			</div>
		</div>
	</section>
</template>


<script>
import OriginalTabDetails from '@/pages/request/review/tab-details.vue';

export default {
	name: 'V2TabDetails',

	extends: OriginalTabDetails,

	computed: {
		advertiserPillClass() {
			const status = this.instance.advertiser_instance && this.instance.advertiser_instance.status;
			if (status === 'approved') return 'v2rt-pill--green';
			if (status === 'awaiting_approval') return 'v2rt-pill--yellow';
			if (status === 'mixed') return 'v2rt-pill--blue';
			return 'v2rt-pill--gray';
		},
		advertiserPillLabel() {
			const status = this.instance.advertiser_instance && this.instance.advertiser_instance.status;
			if (status === 'approved') return 'Approved';
			if (status === 'awaiting_approval') return 'Awaiting';
			if (status === 'mixed') return 'Mixed';
			return status || '';
		},
	},
};
</script>


<style lang="sass" scoped>
@import './tab-shared'

.v2rt
	padding: 0

// ─── Details grid ───
.v2rt-details-grid
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 20px 28px

	@media (max-width: 720px)
		grid-template-columns: 1fr

	&--top
		grid-template-columns: 2fr 1fr 1fr 1fr

		@media (max-width: 920px)
			grid-template-columns: 1fr 1fr

		@media (max-width: 720px)
			grid-template-columns: 1fr

.v2rt-field
	min-width: 0

	&--full
		grid-column: 1 / -1

.v2rt-field-value
	&--big
		font-size: 18px
		font-weight: 700
		color: $text

	&--mono
		font-family: $mono

.v2rt-units
	color: $muted
	font-size: 11px
	margin-left: 2px

.v2rt-divider
	height: 1px
	background: rgba($border, 0.6)
	margin: 24px 0

.v2rt-link
	cursor: pointer
	color: $teal
	font-weight: 500

	&:hover
		text-decoration: underline

.v2rt-pill
	display: inline-flex
	align-items: center
	padding: 2px 8px
	border-radius: 9999px
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.04em

	&--green
		background: rgba($green, 0.12)
		color: $green
	&--yellow
		background: rgba($orange, 0.16)
		color: darken($orange, 8%)
	&--blue
		background: rgba($blue, 0.12)
		color: $blue
	&--gray
		background: rgba($bg, 0.8)
		color: $muted

// ─── Inline edit ───
.v2rt-name-edit
	display: flex
	align-items: center
	gap: 8px

.v2rt-input
	flex: 1
	height: 38px
	padding: 0 14px
	border: 1px solid $border
	border-radius: 10px
	background: rgba($bg, 0.5)
	font-size: 14px
	font-family: $font
	color: $text
	box-sizing: border-box

	&:focus
		outline: none
		border-color: $teal
		background: $card
		box-shadow: 0 0 0 3px rgba($teal, 0.12)

.v2rt-icon-btn
	width: 32px
	height: 32px
	border-radius: 8px
	border: 1px solid $border
	background: $card
	color: $muted
	display: inline-flex
	align-items: center
	justify-content: center
	cursor: pointer
	transition: all 0.15s
	padding: 0

	svg
		width: 14px
		height: 14px

	&:hover
		color: $text
		border-color: $muted

	&--ok
		color: $green
		border-color: rgba($green, 0.4)

		&:hover
			background: rgba($green, 0.06)

	&--cancel
		color: $red
		border-color: rgba($red, 0.4)

		&:hover
			background: rgba($red, 0.06)

	&--edit
		width: 28px
		height: 28px
</style>
